import { createContext, useContext } from "react";

import PropTypes from "prop-types";
import invariant from "tiny-invariant";

import consumerFactory from "./helpers/consumerFactory";
import providerFactory from "./helpers/providerFactory";

export interface CfConfig {
  api: {
    gatewayUrl: string;
    iotUrl: string;
    storesUrl: string;
    sentinelUrl: string;
    geolayersUrl: string;
    satelliteReportsUrl: string;
    lpisLayerName: string;
    maptiler_key: string;
    [key: string]: string;
  };
  environment: string;
}

// PropName which will be supplied to the provider
const propName = "config";

// The name used as a hoc function name
const hocName = "withConfig";

// component prefix for DevTools
const componentPrefix = "CfConfig";

// Shape definition of the provided object. Define ONLY the provided object.
const propTypes = {
  [propName]: PropTypes.shape({
    api: PropTypes.shape({
      gatewayUrl: PropTypes.string.isRequired,
      iotUrl: PropTypes.string.isRequired,
      storesUrl: PropTypes.string.isRequired,
      sentinelUrl: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

const ctx = createContext<CfConfig | null>(null);

if (process.env.NODE_ENV !== "production") {
  ctx.displayName = `${componentPrefix}Context`;
}

const Provider = providerFactory(ctx, propName, propTypes);

/*
 * The Consumer HOC accepts two options:
 * propName: String - if you want to inject the value under different name;
 * forwardRef: Boolean - false
 */
const Consumer = consumerFactory(
  ctx.Consumer,
  propName,
  componentPrefix,
  hocName,
);

export const CfConfigProvider = Provider;
export const useConfig = (): CfConfig => {
  const config = useContext(ctx);

  invariant(config, "useConfig must be used within CfConfigProvider");

  return config;
};
export default Consumer;
